import { defineComponent as _defineComponent } from 'vue'
import {
  ref,
  onMounted,
  onBeforeUnmount,
  computed,
  inject,
  watch,
  Ref,
} from "vue";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import SegmentedControl from "@/components/shared/SegmentedControl.vue";
import { useZoomLevel } from "@/composables/map/useZoomLevel";
import { IBusLocation, ILineInfo, IPath } from "@/domain/entities/lineInfo";
import Page from "@/components/shared/Page.vue";
import { useLineStore } from "@/stores/lineStore";
import {
  BUS_LOCATION_REFRESH_TIME_MILLIS,
  LineTypeCode,
  Location,
  PROVIDE_KEY_CURRENT_LOCATION,
  PROVIDE_KEY_IS_LOCATION_PERMISSION_DENIED,
  PROVIDE_KEY_REMOVE_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_LOADING_IMAGE,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  RemoveLoadingImage,
  ShowErrorModal,
  ShowLoadingImage,
  ShowToastMessage,
} from "@/types";
import CustomSelect from "@/components/shared/selects/CustomSelect.vue";
import {
  FilterKeyValueType,
  IFilter,
  IOption,
} from "@/domain/entities/filter/filter.types";
import useGetLineRouteName from "@/use/useGetLineRouteName";
import { useNaverMapPaths } from "@/composables/map/useNaverMapPaths";
import { useCreateBusStopMarkers } from "@/composables/map/useCreateBusStopMarkers";
import { useNaverMap } from "@/composables/map/useNaverMap";
import Modal from "@/components/shared/modal/base/Modal.vue";
import router from "@/router";
import MapZoomLevelSetter from "@/components/map/MapZoomLevelSetter.vue";
import { useCurrentLocationMarker } from "@/composables/map/useCurrentLocationMarker";
import InlineSvg from "vue-inline-svg";
import { useBusLocationMarkers } from "@/composables/map/useBusLocationMarkers";
import { useBusLocationDrivingStateManager } from "@/composables/line/useBusLocationDrivingStateManager";
import { useBusLocationFetchConfig } from "@/composables/line/useBusLocationFetchConfig";
import dayjs from "dayjs";
import { useServiceStore } from "@/stores/serviceStore";
import deviceHelper from "@/utils/deviceInfo";
import { useShouldGetBusLocation } from "@/composables/map/useShouldGetBusLocation";
import _ from "lodash";
import { useSystemStore } from "@/stores/systemStore";
import getPathsByDate from "@/use/useGetPathsByDate";
import { setIntervalHandler } from "@/utils/setIntervalHandler";

interface ICampus {
  campusCode: string;
  campusName: string;
}

type LineId = string;

const __default__ = {
  name: "InsideShuttleMap",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

const currentLocation = inject(PROVIDE_KEY_CURRENT_LOCATION) as Ref<
  Location | undefined
>;

const isLocationPermissionDenied = inject(
  PROVIDE_KEY_IS_LOCATION_PERMISSION_DENIED
) as Ref<boolean>;

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const showLoadingImage = inject(
  PROVIDE_KEY_SHOW_LOADING_IMAGE
) as ShowLoadingImage;
const removeLoadingImage = inject(
  PROVIDE_KEY_REMOVE_LOADING_IMAGE
) as RemoveLoadingImage;

const serviceStore = useServiceStore();
const systemStore = useSystemStore();
const lineStore = useLineStore();
const isAndroid = deviceHelper.isAndroid();
const hynixCoordinateList = systemStore.campusCoordinateList;

const insideShuttleLineInfos = computed(() => {
  const lineInfos = lineStore.getLineInfosByLineType(LineTypeCode.Inside);

  return lineInfos.sort((a, b) => {
    // 첫번째 Turn의 firstStop의 departureTime을 기준으로 asc 정렬
    const aFirstDepartureTime = a.turns[0]?.firstStop?.departureTime || "";
    const bFirstDepartureTime = b.turns[0]?.firstStop?.departureTime || "";

    if (aFirstDepartureTime !== bFirstDepartureTime) {
      return aFirstDepartureTime.localeCompare(bFirstDepartureTime);
    }

    // routeName asc 순서로 정렬
    return a.routeName.localeCompare(b.routeName);
  });
});

/** 정류장 마커 클릭 시 모든 정류장 이름 표시 여부 */
const showAllBusStopNames = ref(false);

/** 세그먼트 */
const campusList = computed<ICampus[]>(() => {
  const campusMap = new Map<string, ICampus>();

  insideShuttleLineInfos.value.forEach((line) => {
    if (!campusMap.has(line.campusCode)) {
      campusMap.set(line.campusCode, {
        campusCode: line.campusCode,
        campusName: line.campusName,
      });
    }
  });

  return Array.from(campusMap.values());
});

const moveToCampusCenter = () => {
  const coord = hynixCoordinateList.find(
    (coord) => coord.campusName === selectedCampus.value.campusName
  );
  if (coord?.lat && coord.lng) {
    moveMapToCenter(coord.lat, coord.lng);
  }
};

const segmentedControl = ref<typeof SegmentedControl>();
const segments = computed(() =>
  campusList.value.map((campus) => campus.campusName)
);

const selectedCampus = ref<ICampus>(campusList.value[0]);
const selectedSegmentIndex = ref(0);

const onChangedSegmentIndex = async (index: number) => {
  if (selectedSegmentIndex.value === index) return;

  selectedCampus.value = campusList.value[index];
  selectedSegmentIndex.value = index;

  if (selectedCampus.value.campusName !== nearByCampusName.value) {
    toggleShowNotAvailableAlert(true);
    return;
  }

  initSelectedLineInfo();
  moveToCampusCenter();
  await getLineInfoAndRenderPaths();
  await getBusLocations(true);
};

const changeSegmentIndex = (index: number) => {
  segmentedControl.value?.changeSegmentIndex(index);
};

/** 노선 선택 */
const filteredLineInfos = computed((): ILineInfo[] =>
  insideShuttleLineInfos.value.filter(
    (line) => line.campusCode === selectedCampus.value?.campusCode
  )
);

const lineFilter = computed(() => {
  const options = filteredLineInfos.value.map((line, index) => {
    const option: IOption = {
      key: line.id ?? "",
      name: useGetLineRouteName(
        line.lineTypeCode,
        line.routeName,
        line.lineNum
      ),
      default: index === 0,
    };

    return option;
  });

  const filter: IFilter = {
    key: "line",
    type: FilterKeyValueType.FilterKey_OptionKey,
    options,
    availableOptions: options,
    selectedOptions: options.find((option) => !!option.default),
  };

  return filter;
});

const selectedLineInfo = ref<ILineInfo | undefined>(filteredLineInfos.value[0]);

const initSelectedLineInfo = () => {
  const defaultOption = lineFilter.value.availableOptions.find(
    (option) => option.default
  );
  selectedLineInfo.value = filteredLineInfos.value.find(
    (line) => line.id === defaultOption?.key
  );
};

const selectLine = (filter: IFilter, option: IOption) => {
  selectedLineInfo.value = insideShuttleLineInfos.value.find(
    (lineInfo) => lineInfo.id === option.key
  );
};

/** 지도 Zoom Level */
const MIN_ZOOM_LEVEL = 8;
const MAX_ZOOM_LEVEL = 18;

const {
  zoomLevel,
  increaseZoomLevel,
  decreaseZoomLevel,
  addZoomChangedNaverEventListener,
  removeZoomChangedNaverEventListener,
} = useZoomLevel({
  minZoom: MIN_ZOOM_LEVEL,
  maxZoom: MAX_ZOOM_LEVEL,
  initialZoomLevel: 15,
});

/** 지도 */
const { map, addScript, initMap, setCenter, createMarker } = useNaverMap(
  37.2529797,
  127.4806853,
  "#707070",
  zoomLevel.value,
  MIN_ZOOM_LEVEL,
  MAX_ZOOM_LEVEL
);

const moveMapToCenter = (latitude?: number, longitude?: number) => {
  if (!latitude || !longitude) return;
  setCenter(latitude, longitude);
};

/** 현재 위치 마커 */
const {
  currentLocationMarker,
  createCurrentLocationMarker,
  removeCurrentLocationMarker,
} = useCurrentLocationMarker(createMarker);

/** 노선 상세 정보들을 Map객체로 관리 */
const lineInfoDetailMap = ref<Map<LineId, ILineInfo>>(new Map());
const getLineInfoAndRenderPaths = async () => {
  const lineId = selectedLineInfo.value?.id ?? "";
  if (lineInfoDetailMap.value.has(lineId)) {
    drawPathAndBusStopMarker(lineId);
    return;
  }

  const lineInfoResult = await lineStore.getLineInfoDetail(lineId);

  if (!lineInfoResult.isSuccess) {
    showErrorModal(
      "노선 상세 오류",
      lineInfoResult.errorMessage,
      lineInfoResult.resultCode,
      lineInfoResult.errorTime
    );
    return;
  }

  if (lineInfoResult.data) {
    lineInfoDetailMap.value.set(lineId, lineInfoResult.data);
    drawPathAndBusStopMarker(lineId);
  }
};

/** 경로 그리기 */
const { naverMapPaths, drawNaverMapPaths, resetNaverMapPaths } =
  useNaverMapPaths();

/** 정류장 그리기 */
const {
  createBusStopMarkers,
  resetBusStopMarkers,
  updateSelectedBusStopMarkerWithInfoWindow,
} = useCreateBusStopMarkers();

const selectedBusStopId = ref<string | undefined>(undefined);

const handleBusStopClick = (busStop: any) => {
  if (selectedBusStopId.value === busStop.id) {
    selectedBusStopId.value = undefined;
  } else {
    selectedBusStopId.value = busStop.id;
  }
  updateSelectedBusStopMarkerWithInfoWindow(
    selectedBusStopId.value,
    showAllBusStopNames.value
  );
};

const drawPathAndBusStopMarker = (lineId?: string) => {
  if (!lineId) return;

  const lineInfo = lineInfoDetailMap.value.get(lineId);
  if (!lineInfo?.turns.length) return;

  const { detourPaths, busStops } = lineInfo.turns[0];
  const today = dayjs().format("YYYY-MM-DD HH:mm");
  const paths = getPathsByDate(lineInfo.turns[0], today);

  // 경로그리기
  resetNaverMapPaths();
  drawNaverMapPaths(map.value, {
    paths: paths ?? [],
    strokeColor: detourPaths?.length ? "#B3B3B3" : "#8dcd33b3",
  });
  drawDetourPaths(detourPaths, paths);

  // 정류장 마커 그리기
  resetBusStopMarkers();
  createBusStopMarkers(map.value, busStops, undefined, handleBusStopClick);
  if (showAllBusStopNames.value) {
    updateSelectedBusStopMarkerWithInfoWindow(undefined, true);
  }

  // 맵 중심 이동
  moveToCampusCenter();
};

// 우회 경로 그리기
const drawDetourPaths = (detourPaths?: IPath[], paths?: IPath[]) => {
  if (!detourPaths?.length) return;

  const addedPaths = _.differenceWith(
    detourPaths,
    paths ?? [],
    _.isEqual
  ) as IPath[];

  if (!addedPaths.length) return;

  drawNaverMapPaths(map.value, {
    paths: addedPaths,
    strokeColor: "#ff0000",
  });
};

/** 선택 노션 변경이벤트 핸들링 (onSelectOption) */
const handleSelectOption = async (filter: IFilter, option: IOption) => {
  selectLine(filter, option);
  showLoadingImage();
  await getLineInfoAndRenderPaths();
  await getBusLocations(true);
  removeLoadingImage();
};

/** 버스 실시간 위치 마커 */
const { createBusLocationMarkers, removeBusLocationMarkers } =
  useBusLocationMarkers();

/** 모든 호차에 대한 위치정보 가져오기 */
const busLocations = ref<IBusLocation[]>([]);

/** 버스 위치 정보 API의 응답값을 보고, 버스의 주행 상태 파악 */
const {
  updateHasAllBusesArrivedLastStop,
  updateHasAllBusesEndedDriving,

  isBusRunning,
  updateBusRunningStatus,
  shouldGetBusLocation,
} = useBusLocationDrivingStateManager();

const {
  isLoggedIn,
  isLocationCacheExpired,
  lastBusStopArrivalThresholdMin,
  updateBusLocationUpdatedAt,
} = useBusLocationFetchConfig();

/** 실시간 버스 위치 API 호출 */
const getBusLocations = async (lineInfoChanged = false) => {
  if (!selectedLineInfo.value) return;

  const firstTurn = selectedLineInfo.value.turns[0];
  const lastTurn =
    selectedLineInfo.value.turns[selectedLineInfo.value.turns.length - 1];

  const result = await shouldGetBusLocation(
    isLoggedIn.value,
    lastBusStopArrivalThresholdMin.value,
    selectedLineInfo.value.lineTypeCode,
    selectedLineInfo.value.campusName,
    firstTurn?.firstStop,
    lastTurn?.lastStop
  );

  if (!result.shouldGetLocation) {
    busLocations.value = [];
    isBusRunning.value = false;
    return;
  }

  if (!lineInfoChanged && !isLocationCacheExpired()) {
    return;
  }

  const busLocationResult = await lineStore.getBusLocations(
    selectedLineInfo.value?.id ?? ""
  );

  updateBusLocationUpdatedAt(dayjs());
  busLocations.value = busLocationResult.data ?? [];
  updateHasAllBusesArrivedLastStop(busLocations.value);
  updateHasAllBusesEndedDriving(busLocations.value);
  updateBusRunningStatus(busLocations.value, result.shouldGetLocation);
};

// 현재 버스 위치가 없는 경우, 버스 위치 확인 불가 토스트메시지 표출
const showToastIfNoBusLocations = () => {
  if (busLocations.value.length === 0) {
    showToastMessage("현재 운행중인 버스 정보를 찾을 수 없습니다");
  }
};

/** 실시간 버스 위치 갱신 버튼 처리 */
const refreshButton = ref();
const isRotating = ref(false);

const onClickRefreshBtn = async () => {
  if (isRotating.value) return;
  isRotating.value = true;

  setTimeout(() => {
    isRotating.value = false;
  }, 500);

  await getBusLocations();
  showToastIfNoBusLocations();
};

// 가까운 캠퍼스 찾기
const nearByCampusName = ref<string>();

const updateUIForCampus = async (campusName?: string) => {
  if (!campusName) return;

  const index = segments.value.findIndex((s) => s === campusName);
  if (index === -1) return;

  changeSegmentIndex(index);

  showLoadingImage();
  await getLineInfoAndRenderPaths();
  await getBusLocations(true);
  removeLoadingImage();
};

// 이용 불가 모달
const unavailableCampusName = computed(() => {
  if (nearByCampusName.value) {
    return selectedCampus.value.campusName;
  } else {
    return segments.value.join(" / ");
  }
});

const shouldShowNotAvailableAlert = ref(false);
const toggleShowNotAvailableAlert = (show: boolean) => {
  shouldShowNotAvailableAlert.value = show;
};

const goToBack = () => {
  router.currentRoute.value.meta.willGoBack = true;
  router.back();
};

const handleClickOkBtn = () => {
  toggleShowNotAvailableAlert(false);
  if (nearByCampusName.value) {
    updateUIForCampus(nearByCampusName.value);
  } else {
    goToBack();
  }
};

/** 위치 권한 요청 팝업 */
const locationSettingGuide = computed(() => {
  const serviceName = serviceStore.serviceInfo?.name ?? "해피로드";
  return isAndroid
    ? `[설정 > 애플리케이션 > ${serviceName} > 권한 > 위치]`
    : `[설정 > 애플리케이션 > ${serviceName} > 위치]`;
});

const showLocationPermissionConfirmModal = () => {
  showErrorModal(
    "현재 위치 확인 불가",
    `현재 위치를 확인할 수 없습니다.<br />위치 권한이 허용되었는지 확인해주세요.<br />${locationSettingGuide.value}`,
    undefined,
    undefined,
    goToBack
  );
};

const { shouldGetBusLocationAndFindCampus } = useShouldGetBusLocation();

const initInsideShuttleMapByLocation = async () => {
  if (!insideShuttleLineInfos.value.length) {
    showErrorModal(
      "사내셔틀 노선 미존재",
      "사내셔틀 노선이 존재하지 않습니다.",
      undefined,
      undefined,
      goToBack
    );
    return;
  }

  const result = await shouldGetBusLocationAndFindCampus(LineTypeCode.Inside);

  // 현재 위치를 못가져온 경우
  if (result.locationNotAvailable) {
    showLocationPermissionConfirmModal();
    return;
  }

  if (map.value) {
    createCurrentLocationMarker(
      map.value,
      currentLocation.value?.latitude,
      currentLocation.value?.longitude
    );
  }

  nearByCampusName.value = result.nearbyCampusName;
  if (nearByCampusName.value?.length) {
    updateUIForCampus(nearByCampusName.value);
  } else {
    toggleShowNotAvailableAlert(true);
  }

  await setIntervalCallback(getBusLocations);
};

/** 실시간 버스 위치 API Polling */
const busLocationUpdateIntervalMillis = computed(
  () =>
    serviceStore.serviceInfo?.busLocationUpdateIntervalMillis ||
    BUS_LOCATION_REFRESH_TIME_MILLIS
);

const { setIntervalCallback, clearIntervalCallback } = setIntervalHandler(
  busLocationUpdateIntervalMillis.value
);

onMounted(async () => {
  window.naver ? initMap() : addScript();
  await initInsideShuttleMapByLocation();
});

onBeforeUnmount(() => {
  removeZoomChangedNaverEventListener();
  clearIntervalCallback();
});

const isSameBusLocation = (
  newLocations: IBusLocation[],
  oldLocations?: IBusLocation[]
) => {
  if (!oldLocations || newLocations.length !== oldLocations.length)
    return false;

  return newLocations.every((newLocation, index) => {
    const targetOldLocation = oldLocations?.[index];
    return (
      newLocation.lat === targetOldLocation?.lat &&
      newLocation.lng === targetOldLocation?.lng
    );
  });
};

watch(
  () => busLocations.value,
  (newBusLocations, oldBusLocations) => {
    if (!map.value) return;
    if (isSameBusLocation(newBusLocations, oldBusLocations)) return;
    removeBusLocationMarkers();
    createBusLocationMarkers(map.value, newBusLocations, true);
  }
);

watch(
  () => map.value,
  () => {
    removeZoomChangedNaverEventListener();
    if (!map.value) return;

    addZoomChangedNaverEventListener(map.value);
    createCurrentLocationMarker(
      map.value,
      currentLocation.value?.latitude,
      currentLocation.value?.longitude
    );
    createBusLocationMarkers(map.value, busLocations.value, true);
    if (!naverMapPaths.value.length) {
      drawPathAndBusStopMarker(selectedLineInfo.value?.id);
    }
  }
);

/** 위치 권한 거부 여부 */
watch(
  () => isLocationPermissionDenied.value,
  (isLocationPermissionDenied) => {
    if (isLocationPermissionDenied) {
      showLocationPermissionConfirmModal();
    }
  }
);

// showAllBusStopNames 변경 시 마커 업데이트
// watch(
//   () => showAllBusStopNames.value,
//   (newValue) => {
//     // showAllBusStopNames 변경 시 항상 모든 마커를 업데이트
//     updateSelectedBusStopMarkerWithInfoWindow(
//       selectedBusStopId.value,
//       newValue
//     );
//   }
// );

const __returned__ = { currentLocation, isLocationPermissionDenied, showErrorModal, showToastMessage, showLoadingImage, removeLoadingImage, serviceStore, systemStore, lineStore, isAndroid, hynixCoordinateList, insideShuttleLineInfos, showAllBusStopNames, campusList, moveToCampusCenter, segmentedControl, segments, selectedCampus, selectedSegmentIndex, onChangedSegmentIndex, changeSegmentIndex, filteredLineInfos, lineFilter, selectedLineInfo, initSelectedLineInfo, selectLine, MIN_ZOOM_LEVEL, MAX_ZOOM_LEVEL, zoomLevel, increaseZoomLevel, decreaseZoomLevel, addZoomChangedNaverEventListener, removeZoomChangedNaverEventListener, map, addScript, initMap, setCenter, createMarker, moveMapToCenter, currentLocationMarker, createCurrentLocationMarker, removeCurrentLocationMarker, lineInfoDetailMap, getLineInfoAndRenderPaths, naverMapPaths, drawNaverMapPaths, resetNaverMapPaths, createBusStopMarkers, resetBusStopMarkers, updateSelectedBusStopMarkerWithInfoWindow, selectedBusStopId, handleBusStopClick, drawPathAndBusStopMarker, drawDetourPaths, handleSelectOption, createBusLocationMarkers, removeBusLocationMarkers, busLocations, updateHasAllBusesArrivedLastStop, updateHasAllBusesEndedDriving, isBusRunning, updateBusRunningStatus, shouldGetBusLocation, isLoggedIn, isLocationCacheExpired, lastBusStopArrivalThresholdMin, updateBusLocationUpdatedAt, getBusLocations, showToastIfNoBusLocations, refreshButton, isRotating, onClickRefreshBtn, nearByCampusName, updateUIForCampus, unavailableCampusName, shouldShowNotAvailableAlert, toggleShowNotAvailableAlert, goToBack, handleClickOkBtn, locationSettingGuide, showLocationPermissionConfirmModal, shouldGetBusLocationAndFindCampus, initInsideShuttleMapByLocation, busLocationUpdateIntervalMillis, setIntervalCallback, clearIntervalCallback, isSameBusLocation, NavigationBar, SegmentedControl, Page, CustomSelect, Modal, MapZoomLevelSetter, get InlineSvg() { return InlineSvg } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})